"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Link from "next/link";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import { LivescoreMatchGroup } from "@/views/livescore/components/LivescoreMatchGroup";
import { applyGroupOrdering } from "@/views/livescore/utils/grouping";
import { useFavouriteLeagues } from "@/views/livescore/hooks/useFavouriteLeagues";
import { useHomeLivescoreData } from "./HomeLivescoreProvider";

/** Max competition groups shown in the widget */
const MAX_GROUPS = 5;

type WidgetFilter = "popular" | "my-leagues";

interface LiveScoreWidgetProps {
    popularCompetitionIds: string[];
    livescoreUrl: string;
}

export function LiveScoreWidget({ popularCompetitionIds, livescoreUrl }: LiveScoreWidgetProps) {
    const labels = useLabels();
    const ls = labels.livescore;

    const [filter, setFilter] = useState<WidgetFilter>("popular");
    const [isMobile, setIsMobile] = useState(false);

    const { favouriteIds, save: saveFavourites, loaded: favouritesLoaded } = useFavouriteLeagues();
    const initialFilterApplied = useRef(false);

    useEffect(() => {
        const mql = window.matchMedia("(max-width: 767px)");
        setIsMobile(mql.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, []);

    const { groups: allGroups, entities, loading, oddsMap, tvByMatch, anyTvForDay } = useHomeLivescoreData();

    // Football-only widget — filter to football groups before ordering.
    const footballGroups = useMemo(
        () => allGroups.filter((g) => g.sport.toLowerCase() === "football"),
        [allGroups]
    );

    useEffect(() => {
        if (!favouritesLoaded || initialFilterApplied.current) return;
        initialFilterApplied.current = true;
        if (favouriteIds.length > 0) setFilter("my-leagues");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favouritesLoaded]);

    const groups = useMemo(
        () => applyGroupOrdering(footballGroups, filter, popularCompetitionIds, favouriteIds),
        [footballGroups, filter, popularCompetitionIds, favouriteIds]
    );

    const displayedGroups = groups.slice(0, MAX_GROUPS);
    const hiddenCount = Math.max(0, groups.length - MAX_GROUPS);
    const showMyLeaguesEmpty = filter === "my-leagues" && favouritesLoaded && favouriteIds.length === 0;

    // `tvByMatch` + `anyTvForDay` come from HomeLivescoreProvider, which fetches TV listings
    // for the day in parallel with matches/odds — no extra round-trip here.

    const toggleFavourite = useCallback(
        (competitionId: string) => {
            const next = favouriteIds.includes(competitionId)
                ? favouriteIds.filter((id) => id !== competitionId)
                : [...favouriteIds, competitionId];
            saveFavourites(next);
        },
        [favouriteIds, saveFavourites]
    );

    return (
        <section className="space-y-3">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{ls.title}</h2>
                <Link prefetch={false} href={livescoreUrl} className="text-sm link-themed hover:underline">
                    {labels.homePage.viewAll}
                </Link>
            </div>

            <PillToggle
                options={[
                    { id: "popular" as WidgetFilter, label: ls.filters.popular },
                    { id: "my-leagues" as WidgetFilter, label: ls.filters.myLeagues },
                ]}
                activeId={filter}
                onChange={setFilter}
                size="sm"
            />

            {loading ? (
                <div className="text-center py-8 text-muted-foreground text-sm">{ls.loading}</div>
            ) : showMyLeaguesEmpty ? (
                <div className="text-center py-8 text-muted-foreground text-sm space-y-3">
                    <p>{ls.picker.noFavourites}</p>
                    <Link
                        prefetch={false}
                        href={livescoreUrl}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                    >
                        {labels.homePage.viewAll}
                    </Link>
                </div>
            ) : displayedGroups.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">{ls.noMatches}</div>
            ) : (
                <div className="space-y-3">
                    {displayedGroups.map((group) => (
                        <LivescoreMatchGroup
                            key={group.competitionId}
                            group={group}
                            entities={entities}
                            sport="football"
                            filter={filter}
                            oddsMap={oddsMap}
                            isMobile={isMobile}
                            tvByMatch={tvByMatch}
                            reserveTvSlot={anyTvForDay}
                            isFavourite={favouriteIds.includes(group.competitionId)}
                            onToggleFavourite={toggleFavourite}
                        />
                    ))}
                    {hiddenCount > 0 && (
                        <Link
                            prefetch={false}
                            href={livescoreUrl}
                            className="block w-full py-2.5 text-sm link-themed border border-border rounded-md hover:bg-muted transition-colors text-center cursor-pointer"
                        >
                            {ls.showMoreCompetitions.replace("{count}", String(hiddenCount))}
                        </Link>
                    )}
                </div>
            )}
        </section>
    );
}
